import { useCallback, useEffect } from 'react';

import { useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';

import { QUERY_KEYS } from '@constants/query-keys';
import { AlertType } from '@enums/alert';
import { usePusher } from '@hooks/use-pusher';
import { showAlert } from '@lib/alert/alert-manager';
import {
  BULK_WORKSPACE_CREATION_FAILED_STATUS,
  BULK_WORKSPACE_CREATION_PARTIAL_SUCCESS_STATUS,
  BULK_WORKSPACE_CREATION_SUCCESS_STATUS,
} from '@pages/workspace/constants';
import { bulkWorkspaceCreationProgressAtom } from '@store/atoms/bulk-workspace-creation-progress';
import { userProfileAtom } from '@store/atoms/user';
import { workspaceAtom } from '@store/atoms/workspace';

import { useProgress } from './use-progress';

export interface BulkWorkspaceProgress {
  user_id: number;
  progress_percent: number;
  upload_id: number;
  status?: number;
  error?: string;
}

export const useBulkWorkspaceCreationProgress = () => {
  const queryClient = useQueryClient();

  const currentWorkspace = useRecoilValue(workspaceAtom);
  const user = useRecoilValue(userProfileAtom);
  const { bindEvent, unbindEvent } = usePusher(currentWorkspace.id);
  const { items, upsert, remove } = useProgress(
    bulkWorkspaceCreationProgressAtom,
  );

  const handleProgress = useCallback(
    (data: BulkWorkspaceProgress) => {
      if (data.user_id !== user.id) return;

      console.info('Bulk workspace creation progress data:', data);
      const progressItem = {
        name: data.upload_id.toString(),
        progressPercentage: data.progress_percent,
      };

      if (data.status === BULK_WORKSPACE_CREATION_FAILED_STATUS) {
        showAlert({
          message:
            data?.error ||
            'Bulk workspace creation failed. Please check the report.',
          type: AlertType.ERROR,
        });
        remove(progressItem);
      } else if (
        data.status === BULK_WORKSPACE_CREATION_SUCCESS_STATUS ||
        data.status === BULK_WORKSPACE_CREATION_PARTIAL_SUCCESS_STATUS
      ) {
        showAlert({
          message: 'Bulk workspace creation is completed.',
          type: AlertType.SUCCESS,
        });
        queryClient.invalidateQueries(QUERY_KEYS.WORKSPACE.WORKSPACE_LIST);
        remove(progressItem);
      } else {
        upsert(progressItem);
      }
    },
    [upsert, remove],
  );

  useEffect(() => {
    if (!currentWorkspace.id) return;

    bindEvent('BULK_CREATE_WORKSPACES:PROGRESS:', handleProgress);

    return () => {
      unbindEvent('BULK_CREATE_WORKSPACES:PROGRESS:');
    };
  }, [currentWorkspace.id, bindEvent, unbindEvent, handleProgress]);

  return {
    items,
  };
};
